import type {
  CompanyProfile,
  CompanyReviewsAISummary,
  Reviews,
} from '@seek/libs-shared';
import type { LoaderFunction } from 'react-router';

import type { RouteProps } from '../../../shared/RouteProps';
import type { CompaniesListModel } from '../../models/companiesListModel';

import type { AboutModel } from './aboutModel';

const REVIEWS_PER_PAGE = 3;

const mapAboutModel = ({
  companyProfile,
  randomCompaniesInThisIndustry,
  reviews,
  companyReviewsAISummary,
}: {
  companyProfile: CompanyProfile;
  randomCompaniesInThisIndustry: CompaniesListModel;
  companyReviewsAISummary?: CompanyReviewsAISummary;
  reviews?: Reviews;
}): AboutModel => ({
  name: companyProfile.name,
  overview: companyProfile.overview,
  reviewSummary: {
    ratingBreakdown: companyProfile.ratingBreakdown,
    salaryRating: companyProfile.salaryRating,
    employeeRecommendationRating: companyProfile.employeeRecommendationRating,
    companyReviewSummaryBreakdown: companyProfile.companyReviewSummaryBreakdown,
    ...(companyProfile.overallRating
      ? {
          overallRating: {
            average: companyProfile.overallRating?.average,
            count: companyProfile.overallRating?.count,
          },
        }
      : {}),
  },
  featured: companyProfile.featured,
  awards: companyProfile.awards?.map((award) => ({
    ...award,
    name: award.name || '',
  })),
  missionStatement: companyProfile.missionStatement,
  gallery: companyProfile.gallery,
  branding: companyProfile.branding,
  randomCompaniesInThisIndustry,
  reviews,
  companyReviewsAISummary,
});

const extractCompanyIdFromSlug = (input?: string): string | undefined => {
  if (!input) {
    return undefined;
  }
  const parts = input.split('-');
  return parts[parts.length - 1];
};

export const aboutLoader =
  ({ services }: RouteProps): LoaderFunction =>
  async ({ params, request }): Promise<AboutModel> => {
    const companyId = extractCompanyIdFromSlug(params.companySlug);

    if (companyId === undefined) {
      throw new Error('Could not find companyId from params', params);
    }
    const url = new URL(request.url);
    const isDraft = url.searchParams.get('draft');

    const companyProfile =
      await services.companyProfileService.getCompanyProfile(
        companyId,
        Boolean(isDraft),
      );

    const companyReviewsAISummary =
      await services.companyDetailsService.getCompanyReviewsAISummaryById(
        companyId,
      );

    const formattedDateInUTC = new Date().toISOString().split('T')[0];

    const randomCompaniesInThisIndustry =
      await services.companyProfileService.getGroupedCompanies({
        groupBy: {
          group: 'INDUSTRY',
          value: [companyProfile.overview?.industry || ''],
        },
        perPage: 13,
        excludedCompanyIds: [companyId],
        randomSeed: formattedDateInUTC,
      });

    let reviews;
    if (!isDraft) {
      reviews = await services.companyProfileService.getReviews({
        companyId,
        page: 1,
        sortOrder: 'most recent',
        perPage: REVIEWS_PER_PAGE,
      });
    }

    return mapAboutModel({
      companyProfile,
      randomCompaniesInThisIndustry,
      reviews,
      companyReviewsAISummary: companyReviewsAISummary ?? undefined,
    });
  };
